import { formatISO, subDays } from 'date-fns';

import { ImageArtifact } from '@custom-types/Configuration';
import { Query } from '@custom-types/Query';
import { Response } from '@custom-types/Response';
import apiFn from '@services/api-service';

export const HAPPENING = 'happening';
export const UPCOMING = 'upcoming';
export const CONCLUDED = 'concluded';
export type EventGroup = typeof HAPPENING | typeof UPCOMING | typeof CONCLUDED;

export interface Event {
  id: string;
  code: string;
  name: string;
  locationName: string;
  startDate: string;
  endDate: string;
  group: EventGroup;
  eventIcon: {
    externalId: string;
    fullSizeUrl: string;
    id: string;
    standardSizeUrl: string;
    thumbnailUrl: string;
  };
}

export interface ConfigurationData {
  configuration: {
    badgePrintingOnsiteLabelsEnabled: '0' | '1';
    checkinEnableKioskMode: '0' | '1';
    checkinEnableCheckout: '0' | '1';
    checkinEnableSearchByNameOrEmail: '0' | '1';
    checkinKioskColor: string;
    checkinShowPeopleGroups: '0' | '1';
  };
  id: string;
  badgePrintingImage: ImageArtifact | null;
  idleScreenImage: ImageArtifact | null;
  onsiteLabelPrintingBadges: Array<{
    id: string;
    name: string;
    order: number;
    previewImage: string | null;
  }>;
}

export enum ErrorCode {
  OnsiteAppNotEnabled = 'onsite_app_not_enabled',
}

enum Include {
  EventIcon = 'event_icon',
}

const DEFAULT_INCLUDE = Include.EventIcon;

const leadCaptureApi = () => apiFn('/api/uapi/onsite-app/events');
const api = () => apiFn(`/api/uapi/events`);
const staffApi = () => apiFn('/api/uapi/admin/events');
const configApi = (eventId: string) => apiFn(`/api/uapi/events/${eventId}/onsite-app/settings`);

const transform = (event: Event) => {
  const startDate = new Date(event.startDate);
  const endDate = new Date(event.endDate);
  const now = new Date();

  Object.assign(event, { id: event.id.toString() });

  if (endDate < now) {
    Object.assign(event, { group: CONCLUDED });
  } else if (startDate > now) {
    Object.assign(event, { group: UPCOMING });
  } else {
    Object.assign(event, { group: HAPPENING });
  }

  return event;
};

export const list = (page: number, searchValue?: string) => {
  const endDateAfter = formatISO(subDays(Date.now(), 1), { representation: 'date' });

  const params = {
    featureEnabled: 'is_checkin_enabled',
    include: DEFAULT_INCLUDE,
    sort: 'start_date',
    endDateAfter,
    page,
  };

  if (searchValue) {
    Object.assign(params, { search: searchValue });
  }

  return api()
    .get('', { params })
    .then(({ data: { data, meta } }: Response<Event[]>) => ({
      // TODO: EXP-10817 - following transformation will be removed
      data: [...data.map(event => transform(event))],
      meta,
    }));
};

export const listForLeadCapture = (page: number, searchValue?: string) => {
  const endDateAfter = formatISO(subDays(Date.now(), 30), { representation: 'date' });

  const params = {
    include: DEFAULT_INCLUDE,
    sort: 'start_date',
    endDateAfter,
    page,
  };

  if (searchValue) {
    Object.assign(params, { search: searchValue });
  }

  return leadCaptureApi()
    .get('', { params })
    .then(({ data: { data, meta } }: Response<Event[]>) => ({
      data: [...data.map(event => transform(event))],
      meta,
    }));
};

export enum Field {
  Id = 'id',
  Code = 'code',
}

type SearchField = Partial<{ [Field.Id]: string; [Field.Code]: string }>;

export const listForStaff = (query: Query & SearchField) => {
  const params = {
    sort: 'start_date',
    limit: 10,
    include: DEFAULT_INCLUDE,
    ...query,
  };

  return staffApi()
    .get('', { params })
    .then((response: Response<Event[]>) => response.data);
};

export const get = (eventId: string) =>
  api()
    .get(eventId, {
      params: {
        include: DEFAULT_INCLUDE,
      },
    })
    .then((response: Response<Event>) => response.data.data);

export const getConfiguration = (eventId: string) =>
  configApi(eventId)
    .get('')
    .then((response: Response<ConfigurationData>) => response.data.data);
